/**
 * Production container for the medical-records export builder.
 *
 * Owns the data boundary (generated contract via `useMedicalRecordsExport`),
 * the options loading/error states, and PHI-free Statsig tracking; renders the
 * presentation dialog. Both patient-page generations mount this with their
 * own launch affordance.
 */
import { useEffect, useRef } from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { MedicalRecordsExportDialog } from './MedicalRecordsExportDialog';
import type { GeneratedMedicalRecordPacket, MedicalRecordsExportSelection } from './types';
import { useMedicalRecordsExport } from './useMedicalRecordsExport';

export interface MedicalRecordsExportContainerProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  subjectName: string;
  subjectDetails?: string;
  /** Launch surface, for analytics only. */
  source: 'patient_page_v1' | 'patient_page_v2' | 'patient_page_v3';
}

export function MedicalRecordsExportContainer({
  open,
  onClose,
  patientId,
  subjectName,
  subjectDetails,
  source,
}: MedicalRecordsExportContainerProps) {
  const { medicalRecordsExport: exportMetrics } = useEventTracking();
  const {
    encounters,
    documents,
    excludedNoteCount,
    isLoadingOptions,
    optionsError,
    refetchOptions,
    generatePacket,
    downloadPacket,
  } = useMedicalRecordsExport({ patientId, enabled: open });

  const generateAttemptRef = useRef(0);

  useEffect(() => {
    if (open) {
      generateAttemptRef.current = 0;
      exportMetrics.dialogOpened({ patient_id: patientId, source });
    }
    // exportMetrics is a stable tracking facade; patientId/source only change with the dialog target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleGenerate = async (
    selection: MedicalRecordsExportSelection,
  ): Promise<GeneratedMedicalRecordPacket> => {
    generateAttemptRef.current += 1;
    exportMetrics.generationStarted({
      patient_id: patientId,
      output_mode: selection.outputMode,
      encounter_count: selection.encounterIds.length,
      note_count: selection.noteIds.length,
      document_count: selection.documentReferenceIds.length,
      attempt: generateAttemptRef.current,
    });
    return generatePacket(selection);
  };

  const handleDownload = (packet: GeneratedMedicalRecordPacket): Promise<void> => {
    exportMetrics.downloadRequested({
      patient_id: patientId,
      export_id: packet.exportId ?? '',
      output_mode: packet.outputMode,
    });
    // Return the promise so the dialog can surface an expired/revoked/failed
    // download (throws from downloadPacket) as an actionable error on the ready
    // step rather than swallowing it as an unhandled rejection.
    return downloadPacket(packet);
  };

  if (!open) return null;

  if (isLoadingOptions || optionsError) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent>
          {isLoadingOptions ? (
            <Stack spacing={1.5} sx={{ alignItems: 'center', py: 6 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading exportable records…
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ alignItems: 'center', py: 6 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                The exportable records could not be loaded.
              </Typography>
              <Box>
                <Button startIcon={<RefreshOutlined />} onClick={() => void refetchOptions()}>
                  Try again
                </Button>
                <Button color="inherit" onClick={onClose}>
                  Close
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <MedicalRecordsExportDialog
      open={open}
      subjectName={subjectName}
      subjectDetails={subjectDetails}
      encounters={encounters}
      documents={documents}
      excludedNoteCount={excludedNoteCount}
      onClose={onClose}
      onGenerate={handleGenerate}
      onDownload={handleDownload}
      onStepChange={step => {
        if (step === 'review') {
          exportMetrics.reviewOpened({ patient_id: patientId });
        }
      }}
      onOutputModeChange={mode =>
        exportMetrics.outputModeChanged({ patient_id: patientId, output_mode: mode })
      }
    />
  );
}
